import type { Database } from './database';

export type SupportCase = Database['public']['Tables']['support_cases']['Row'];
export type SupportCaseInsert = Database['public']['Tables']['support_cases']['Insert'];
export type SupportCaseUpdate = Database['public']['Tables']['support_cases']['Update'];

export type CaseNote = Database['public']['Tables']['case_notes']['Row'];
export type CaseNoteInsert = Database['public']['Tables']['case_notes']['Insert'];

export type CaseStatus = SupportCase['status'];
export type CasePriority = SupportCase['priority'];
export type CaseNoteType = CaseNote['note_type'];

// Case with related data
export interface CaseWithDetails extends SupportCase {
  customer?: {
    id: string;
    first_name: string;
    last_name: string;
    phone_number: string;
    email: string | null;
  };
  assigned_to_staff?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  closed_by_staff?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  notes?: CaseNote[];
  event?: {
    id: string;
    event_type: string;
    properties: any;
    created_at: string;
  };
}

// Case filters
export interface CaseFilters {
  status?: CaseStatus | 'all';
  priority?: CasePriority | 'all';
  assigned_to?: string | 'me' | 'unassigned' | 'all';
  case_type?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
}

// Case creation data
export interface CreateCaseData {
  user_id: string;
  case_type: string;
  priority: CasePriority;
  customer_message?: string;
  send_sms: boolean;
  sms_template_id?: string;
}

// Case update data
export interface UpdateCaseData {
  status?: CaseStatus;
  priority?: CasePriority;
  assigned_to?: string;
  resolution?: string;
}

// Helper functions
export const getCasePriorityColor = (priority: CasePriority): string => {
  const colors: Record<CasePriority, string> = {
    p1: 'red',
    p2: 'orange',
    p3: 'gray',
  };
  return colors[priority];
};

export const getCasePriorityLabel = (priority: CasePriority): string => {
  const labels: Record<CasePriority, string> = {
    p1: 'High',
    p2: 'Medium',
    p3: 'Normal',
  };
  return labels[priority];
};

export const getCaseStatusColor = (status: CaseStatus): string => {
  const colors: Record<CaseStatus, string> = {
    open: 'red',
    pending: 'orange',
    closed: 'green',
  };
  return colors[status];
};

export const getCaseStatusLabel = (status: CaseStatus): string => {
  const labels: Record<CaseStatus, string> = {
    open: 'Open',
    pending: 'Pending',
    closed: 'Closed',
  };
  return labels[status];
};

export const getCaseTypeLabel = (caseType: string): string => {
  const labels: Record<string, string> = {
    payment_failure: 'Payment Failure',
    account_locked: 'Account Locked',
    kyc_expiry: 'KYC Document Expiry',
    card_issue: 'Card Issue',
    failed_login: 'Failed Login',
    large_transaction: 'Large Transaction',
    international_transfer: 'International Transfer',
    general: 'General Inquiry',
  };
  return labels[caseType] || caseType;
};

// Calculate SLA status
export interface SLAStatus {
  status: 'ok' | 'warning' | 'overdue';
  hours_remaining: number;
  percentage_elapsed: number;
}

export const calculateSLAStatus = (
  createdAt: string,
  slaDueAt: string | null,
  status: CaseStatus
): SLAStatus => {
  if (status === 'closed' || !slaDueAt) {
    return { status: 'ok', hours_remaining: 0, percentage_elapsed: 0 };
  }

  const now = new Date();
  const created = new Date(createdAt);
  const due = new Date(slaDueAt);
  
  const totalMs = due.getTime() - created.getTime();
  const elapsedMs = now.getTime() - created.getTime();
  const remainingMs = due.getTime() - now.getTime();
  
  const hoursRemaining = remainingMs / (1000 * 60 * 60);
  const percentageElapsed = (elapsedMs / totalMs) * 100;

  let slaStatus: 'ok' | 'warning' | 'overdue';
  if (remainingMs < 0) {
    slaStatus = 'overdue';
  } else if (hoursRemaining < 2) {
    slaStatus = 'warning';
  } else {
    slaStatus = 'ok';
  }

  return {
    status: slaStatus,
    hours_remaining: Math.max(0, hoursRemaining),
    percentage_elapsed: Math.min(100, percentageElapsed),
  };
};

